import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Tag,
  Input as AntInput,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  BankOutlined,
  PhoneOutlined,
  MailOutlined,
  MobileOutlined,
  WhatsAppOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { companyService, Company, CreateCompanyRequest, UpdateCompanyRequest } from '../services/companyService';
import { useI18n } from '../hooks/useI18n';

const { Search } = AntInput;
const { TextArea } = Input;

const CompanyManagementPage: React.FC = () => {
  const { t } = useI18n();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');

  const currentTenant = localStorage.getItem('currentTenant') || 'default';

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await companyService.getCompanies(currentTenant);
      if (response.success) {
        setCompanies(response.data || []);
      } else {
        message.error(t('companyManagement.loadFailed'));
      }
    } catch (error: any) {
      message.error(t('companyManagement.loadFailed') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCompany(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    form.setFieldsValue({
      company_name: company.company_name,
      database_name: company.database_name,
      project_id: company.project_id ?? '',
      description: company.description,
      email: company.email,
      whatsapp: company.whatsapp,
      mobile: company.mobile,
      alias: company.alias,
      is_active: Boolean(company.is_active),
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      // 注意：后端可能没有删除接口，这里先注释掉
      // const response = await companyService.deleteCompany(currentTenant, id);
      // if (response.success) {
      //   message.success('公司删除成功');
      //   loadCompanies();
      // } else {
      //   message.error(t('companyManagement.deleteFailed'));
      // }
      message.warning(t('companyManagement.deleteNotImplemented'));
    } catch (error: any) {
      message.error(t('companyManagement.deleteFailed') + ': ' + error.message);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const requestData = {
        ...values,
        is_active: values.is_active ? 1 : 0,
      };

      if (editingCompany) {
        // 更新公司
        const response = await companyService.updateCompany(
          currentTenant,
          editingCompany.id,
          requestData
        );
        if (response.success) {
          message.success(t('companyManagement.updateSuccess'));
          setModalVisible(false);
          loadCompanies();
        } else {
          message.error(t('companyManagement.updateFailed'));
        }
      } else {
        // 创建公司
        const response = await companyService.createCompany(currentTenant, requestData);
        if (response.success) {
          message.success(t('companyManagement.createSuccess'));
          setModalVisible(false);
          loadCompanies();
        } else {
          message.error(t('companyManagement.createFailed'));
        }
      }
    } catch (error: any) {
      message.error((editingCompany ? t('companyManagement.updateFailed') : t('companyManagement.createFailed')) + ': ' + error.message);
    }
  };

  const handleSearch = (value: string) => {
    if (!value.trim()) {
      loadCompanies();
      return;
    }

    const filtered = companies.filter((company) => {
      const searchLower = value.toLowerCase();
      return (
        company.company_name?.toLowerCase().includes(searchLower) ||
        company.database_name?.toLowerCase().includes(searchLower) ||
        company.alias?.toLowerCase().includes(searchLower) ||
        company.email?.toLowerCase().includes(searchLower) ||
        company.whatsapp?.includes(value) ||
        company.mobile?.includes(value)
      );
    });

    setCompanies(filtered);
  };

  const columns = [
    {
      title: t('companyManagement.companyName'),
      dataIndex: 'company_name',
      key: 'company_name',
      width: 150,
      render: (text: string) => (
        <Space>
          <BankOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: t('companyManagement.databaseName'),
      dataIndex: 'database_name',
      key: 'database_name',
      width: 150,
    },
    {
      title: t('companyManagement.alias'),
      dataIndex: 'alias',
      key: 'alias',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: t('companyManagement.projectId'),
      dataIndex: 'project_id',
      key: 'project_id',
      width: 120,
      render: (text: any) => (text === null || text === undefined || String(text).trim() === '' ? '-' : String(text)),
    },
    {
      title: t('companyManagement.description'),
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        text ? (
          <Space>
            <FileTextOutlined />
            <span title={text}>{text}</span>
          </Space>
        ) : '-'
      ),
    },
    {
      title: t('companyManagement.email'),
      dataIndex: 'email',
      key: 'email',
      width: 180,
      render: (text: string) => (
        text ? (
          <Space>
            <MailOutlined />
            {text}
          </Space>
        ) : '-'
      ),
    },
    {
      title: t('companyManagement.whatsapp'),
      dataIndex: 'whatsapp',
      key: 'whatsapp',
      width: 130,
      render: (text: string) => (
        text ? (
          <Space>
            <WhatsAppOutlined />
            {text}
          </Space>
        ) : '-'
      ),
    },
    {
      title: t('companyManagement.mobile'),
      dataIndex: 'mobile',
      key: 'mobile',
      width: 130,
      render: (text: string) => (
        text ? (
          <Space>
            <MobileOutlined />
            {text}
          </Space>
        ) : '-'
      ),
    },
    {
      title: t('companyManagement.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive: number) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('companyManagement.enabled') : t('companyManagement.disabled')}
        </Tag>
      ),
    },
    {
      title: t('companyManagement.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: t('companyManagement.actions'),
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Company) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('companyManagement.edit')}
          </Button>
          <Popconfirm
            title={t('companyManagement.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.ok')}
            cancelText={t('common.cancel')}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              {t('companyManagement.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <h2 style={{ margin: 0 }}>
              <BankOutlined style={{ marginRight: 8 }} />
              {t('companyManagement.title')}
            </h2>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('companyManagement.addCompany')}
            </Button>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Search
              placeholder={t('companyManagement.searchPlaceholder')}
              enterButton={<SearchOutlined />}
              size="middle"
              onSearch={handleSearch}
              onChange={(e) => {
                setSearchText(e.target.value);
                if (!e.target.value.trim()) {
                  loadCompanies();
                }
              }}
              value={searchText}
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={companies}
          rowKey="id"
          loading={loading}
          pagination={{
            total: companies.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('companyManagement.totalRecords', { total }),
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      <Modal
        title={editingCompany ? t('companyManagement.editCompany') : t('companyManagement.addCompany')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="company_name"
                label={t('companyManagement.companyName')}
                rules={[{ required: true, message: t('companyManagement.companyNameRequired') }]}
              >
                <Input placeholder={t('companyManagement.companyNameRequired')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="database_name"
                label={t('companyManagement.databaseName')}
                rules={[{ required: true, message: t('companyManagement.databaseNameRequired') }]}
              >
                <Input placeholder={t('companyManagement.databaseNameRequired')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="project_id"
            label={t('companyManagement.projectId')}
          >
            <Input placeholder={t('companyManagement.projectIdPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="alias"
            label={t('companyManagement.alias')}
          >
            <Input placeholder={t('companyManagement.alias')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('companyManagement.description')}
          >
            <TextArea 
              placeholder={t('companyManagement.description')} 
              rows={3}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label={t('companyManagement.email')}
                rules={[
                  { type: 'email', message: t('companyManagement.emailInvalid') }
                ]}
              >
                <Input placeholder={t('companyManagement.email')} prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="mobile"
                label={t('companyManagement.mobile')}
              >
                <Input placeholder={t('companyManagement.mobile')} prefix={<MobileOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="whatsapp"
            label={t('companyManagement.whatsapp')}
          >
            <Input placeholder={t('companyManagement.whatsapp')} prefix={<WhatsAppOutlined />} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('companyManagement.status')}
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren={t('companyManagement.enabled')} unCheckedChildren={t('companyManagement.disabled')} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCompany ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyManagementPage;
